import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, message } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  ResourceBasicIcon,
  ResourceImageIcon,
  LocationPinIcon,
  PersonIcon,
  CaretIcon,
} from '../../assets/icons';

/* ------------------------------------------------------------------ *
 * Add Resource (Resource Directory → Add). Figma 1657-674.
 * Three sections — Basic & Categorization, Location & Mapping, Key Person —
 * plus a sticky Discard / Save as Draft / Publish toolbar. Demo (no API yet).
 * ------------------------------------------------------------------ */

const CATEGORIES = ['Education', 'Health', 'Religious', 'Infrastructure', 'Public Safety', 'Community'];
const SUB_CATEGORIES = [
  'Primary School',
  'Secondary School',
  'Senior Secondary School',
  'High School',
  'Hospital',
  'Primary Health Centre',
  'Temple',
  'Community Hall',
];
const RESOURCE_TYPES = ['School', 'College', 'Hospital', 'Clinic', 'Place of Worship', 'Office'];
const LOCATION_LEVELS = ['State', 'District', 'Assembly', 'Block', 'Gram Panchayat', 'Village', 'Booth'];

/** Design-matched field styling (slate-200 border, rounded, focus ring). */
const INPUT =
  'w-full rounded-lg border border-[#e2e8f0] bg-white px-[15px] py-3 text-sm text-ink placeholder:text-[#94a3b8] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="text-xs font-semibold leading-4 text-[#334155]">
      {children}
      {required && <span className="font-semibold text-[#ef4444]"> *</span>}
    </label>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] leading-[16.5px] text-[#94a3b8]">{children}</p>;
}

/** One form section card — icon badge + title/subtitle header, right-side pill, body. */
function SectionCard({
  icon,
  title,
  subtitle,
  badge,
  badgeTone = 'blue',
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  badge: string;
  badgeTone?: 'blue' | 'slate';
  children: React.ReactNode;
}) {
  const badgeCls =
    badgeTone === 'blue'
      ? 'bg-[#eff6ff] border-[#dbeafe] text-[#1d4ed8]'
      : 'bg-[#f1f5f9] border-[#e2e8f0] text-[#475569]';
  return (
    <section className="flex flex-col gap-6 rounded-xl border border-[rgba(226,232,240,0.9)] bg-white p-[25px] shadow-card">
      <header className="flex items-start justify-between gap-4 border-b border-[#f1f5f9] pb-5">
        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#eff6ff] text-brand">
            {icon}
          </span>
          <div className="flex flex-col gap-0.5">
            <h2 className="text-base font-bold leading-6 text-[#0f172a]">{title}</h2>
            <p className="text-xs leading-4 text-[#64748b]">{subtitle}</p>
          </div>
        </div>
        <span className={['shrink-0 rounded-md border px-[11px] py-[3px] text-[11px] font-semibold leading-[16.5px]', badgeCls].join(' ')}>
          {badge}
        </span>
      </header>
      {children}
    </section>
  );
}

export default function AddResourcePage() {
  const navigate = useNavigate();
  const caret = <CaretIcon className="size-3.5 text-[#64748b]" />;

  const [category, setCategory] = useState<string>();
  const [subCategory, setSubCategory] = useState<string>();
  const [resourceType, setResourceType] = useState<string>();
  const [name, setName] = useState('');
  const [locationLevel, setLocationLevel] = useState<string>();
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [officer, setOfficer] = useState('');
  const [designation, setDesignation] = useState('');
  const [mobile, setMobile] = useState('');
  const [altPhone, setAltPhone] = useState('');
  const [email, setEmail] = useState('');

  const publish = () => {
    if (!category || !subCategory || !resourceType || !name.trim()) {
      message.error('Please complete the required Basic Details.');
      return;
    }
    message.success('Resource published.');
    navigate('/admin/resource-directory');
  };

  return (
    <div className="flex flex-col gap-6 pb-24">
      {/* Breadcrumb + header */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Dashboard', to: '/admin' },
            { label: 'Resource Directory', to: '/admin/resource-directory' },
            { label: 'Add Resource' },
          ]}
        />
        <h1 className="pt-1 text-2xl font-semibold leading-8 tracking-[-0.6px] text-ink">
          Add Resources
        </h1>
        <p className="text-sm leading-5 text-ink-secondary">
          Enter details to register a new public facility, institution, or community resource in the
          constituency directory.
        </p>
      </div>

      {/* Card 1 — Basic & Categorization */}
      <SectionCard
        icon={<ResourceBasicIcon className="size-5" />}
        title="1. Basic & Categorization Details"
        subtitle="Core institutional nomenclature, sector classification, and operational status."
        badge="Primary Step"
      >
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <FieldLabel required>Category</FieldLabel>
              <Select
                className="w-full"
                size="large"
                placeholder="Select category"
                value={category}
                onChange={setCategory}
                options={CATEGORIES.map((o) => ({ value: o, label: o }))}
                suffixIcon={caret}
              />
              <Hint>Classifies governance domain for reporting.</Hint>
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel required>Sub-Category</FieldLabel>
              <Select
                className="w-full"
                size="large"
                placeholder="Select specific type"
                value={subCategory}
                onChange={setSubCategory}
                options={SUB_CATEGORIES.map((o) => ({ value: o, label: o }))}
                suffixIcon={caret}
              />
              <Hint>Granular unit definition for caseworkers.</Hint>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <FieldLabel required>Resource Type</FieldLabel>
              <Select
                className="w-full"
                size="large"
                placeholder="Select Type"
                value={resourceType}
                onChange={setResourceType}
                options={RESOURCE_TYPES.map((o) => ({ value: o, label: o }))}
                suffixIcon={caret}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <FieldLabel required>Institution Name</FieldLabel>
              <input
                className={INPUT}
                placeholder="e.g. Government Higher Secondary School, Station Road"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>

          {/* Resource image upload */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel>Resource Image</FieldLabel>
            <button
              type="button"
              onClick={() => message.info('Image upload coming soon.')}
              className="flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed border-[#e2e8f0] bg-[#f8fafc]/50 p-[22px] text-center transition-colors hover:border-brand/50 hover:bg-brand-shade/30"
            >
              <ResourceImageIcon className="size-8 text-[#3b82f6]/80" />
              <span className="pt-1 text-xs">
                <span className="font-semibold text-[#2563eb]">Click to upload</span>
                <span className="font-medium text-[#334155]"> or drag and drop facility photo</span>
              </span>
              <span className="text-[11px] leading-[16.5px] text-[#94a3b8]">
                PNG, JPG, or WEBP up to 5MB (Geo-tagged pictures preferred)
              </span>
            </button>
          </div>
        </div>
      </SectionCard>

      {/* Card 2 — Location & Mapping */}
      <SectionCard
        icon={<LocationPinIcon className="size-5" />}
        title="2. Location"
        subtitle="Electoral zoning, territorial markers, and spatial coordinates."
        badge="GIS Boundary"
        badgeTone="slate"
      >
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
            <div className="flex flex-col gap-1">
              <FieldLabel required>Location Level</FieldLabel>
              <Select
                className="w-full"
                size="large"
                placeholder="Level"
                value={locationLevel}
                onChange={setLocationLevel}
                options={LOCATION_LEVELS.map((o) => ({ value: o, label: o }))}
                suffixIcon={caret}
              />
              <Hint>Constituency hierarchy echelon.</Hint>
            </div>
            <div className="flex flex-col gap-1 md:col-span-2">
              <FieldLabel required>Street Address &amp; Notable Landmark</FieldLabel>
              <input
                className={INPUT}
                placeholder="e.g. Opposite Central Post Office, Station Road"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <Hint>Include cross street, landmark, and physical signage reference.</Hint>
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel required>Postal Index (Pincode)</FieldLabel>
              <input
                className={INPUT}
                placeholder="301001"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
              />
              <Hint>6-digit standard postal code.</Hint>
            </div>
          </div>

          {/* Coordinates + boundary map */}
          <div className="flex flex-col gap-2 pt-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <FieldLabel>Geographical Coordinates &amp; Boundary Map</FieldLabel>
              <span className="inline-flex items-center gap-1.5 rounded border border-[#a7f3d0] bg-[#ecfdf5] px-2.5 py-[3px]">
                <span className="size-1.5 rounded-full bg-[#10b981]" />
                <span className="font-mono text-[11px] text-[#047857]">Lat: 27.5530° N, Long: 76.6346° E</span>
              </span>
            </div>
            {/* Simulated GIS map panel */}
            <div className="relative h-[202px] overflow-hidden rounded-xl border border-[#e2e8f0] bg-gradient-to-br from-[#eef4ff] via-[#f1f5f9] to-[#e2e8f0]">
              {/* Center pin */}
              <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
                <span className="flex size-9 items-center justify-center rounded-full bg-[#2563eb] text-white shadow-[0px_0px_0px_4px_white,0px_10px_15px_-3px_rgba(0,0,0,0.1)]">
                  <LocationPinIcon className="size-5" />
                </span>
                <span className="mt-1 rounded bg-[rgba(15,23,42,0.9)] px-2 py-0.5 text-[10px] font-semibold text-white shadow">
                  Target Facility Pin
                </span>
              </div>
              {/* Floating search */}
              <div className="absolute left-3 top-3 w-64 max-w-[70%] rounded-lg border border-[#e2e8f0] bg-white/95 px-2.5 py-1.5 text-[11px] text-[#94a3b8] backdrop-blur-sm">
                Search coordinates or landmark...
              </div>
              {/* Zoom controls */}
              <div className="absolute bottom-3 right-3 flex flex-col overflow-hidden rounded-lg border border-[#e2e8f0] bg-white/95 backdrop-blur-sm">
                <button type="button" className="border-b border-[#e2e8f0] px-2.5 py-1 text-xs font-bold text-[#334155]">
                  +
                </button>
                <button type="button" className="px-2.5 py-1 text-xs font-bold text-[#334155]">
                  −
                </button>
              </div>
              {/* Map layers label */}
              <div className="absolute bottom-3 left-3 rounded-md border border-[#e2e8f0] bg-white/90 px-2.5 py-1 text-[10px] text-[#475569] backdrop-blur-sm">
                Map Layers: <span className="font-bold">Cadastral GIS + OpenStreetMap</span>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Card 3 — Key Person & In-Charge */}
      <SectionCard
        icon={<PersonIcon className="size-5" />}
        title="3. Key Person & In-Charge Details"
        subtitle="Designated administrative head, emergency point-of-contact, and official outreach."
        badge="Accountability"
        badgeTone="slate"
      >
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <FieldLabel required>In-Charge / Officer Full Name</FieldLabel>
              <div className="relative">
                <PersonIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#94a3b8]" />
                <input
                  className={[INPUT, 'pl-10'].join(' ')}
                  placeholder="e.g. Dr. Rajesh Sharma"
                  value={officer}
                  onChange={(e) => setOfficer(e.target.value)}
                />
              </div>
              <Hint>Full name with prefix (Dr./Er./Shri/Smt.).</Hint>
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel required>Designation / Role Title</FieldLabel>
              <input
                className={INPUT}
                placeholder="e.g. Principal / Chief Medical Officer / SHO"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
              />
              <Hint>Official executive post held at the resource.</Hint>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <FieldLabel required>Primary Mobile Number</FieldLabel>
              <div className="flex h-11 items-stretch overflow-hidden rounded-lg border border-[#e2e8f0]">
                <span className="flex items-center border-r border-[#e2e8f0] bg-[#f8fafc] px-3.5 text-xs font-semibold text-[#475569]">
                  +91
                </span>
                <input
                  className="min-w-0 flex-1 px-3.5 text-sm text-ink placeholder:text-[#94a3b8] focus:outline-none"
                  placeholder="98765 43210"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                />
              </div>
              <Hint>Direct mobile used for emergency notifications.</Hint>
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel>Alternate Phone / Landline</FieldLabel>
              <input
                className={INPUT}
                placeholder="e.g. 0144-234567"
                value={altPhone}
                onChange={(e) => setAltPhone(e.target.value)}
              />
              <Hint>Office front desk or reception desk line.</Hint>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <FieldLabel>Official Email Address</FieldLabel>
            <input
              type="email"
              className={INPUT}
              placeholder="e.g. officer.alwar@rajasthan.gov.in"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Hint>Preferred official .gov.in or authorized institutional mailbox.</Hint>
          </div>
        </div>
      </SectionCard>

      {/* Sticky action toolbar */}
      <div className="sticky bottom-0 z-10 -mx-4 flex items-center justify-end gap-2.5 border-t border-line-soft bg-white px-4 py-4 sm:-mx-6 sm:px-6">
        <button
          type="button"
          onClick={() => navigate('/admin/resource-directory')}
          className="h-10 rounded-lg bg-brand-shade px-4 text-[13px] font-medium text-[#0b1c30] transition-colors hover:bg-[#e0ebff]"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={() => {
            message.success('Saved as draft.');
            navigate('/admin/resource-directory');
          }}
          className="h-10 rounded-lg border border-brand px-4 text-[13px] font-medium text-brand transition-colors hover:bg-brand-shade"
        >
          Save as Draft
        </button>
        <button
          type="button"
          onClick={publish}
          className="h-10 rounded-lg bg-brand px-5 text-[13px] font-medium text-white shadow-card transition-colors hover:bg-brand-dark"
        >
          Publish Resource
        </button>
      </div>
    </div>
  );
}
